/**
 * Process-lifetime cache for the "every vendor on this branch" list.
 *
 * Five 1099 screens each fetched the whole vendor list on mount, so moving
 * between Portfolio, Payments and Year-End Review re-fetched the same 547 rows
 * three times over, three requests each.
 *
 * Deliberately not a general-purpose cache:
 *  - Keyed by branch and tax year, because Year-End Review lets the user pick a
 *    year and the row's ytd_gross and threshold are year-scoped. A single
 *    cached list would serve one year's figures to another year's screen.
 *  - No TTL. Time is the wrong trigger here -- these rows back a compliance
 *    screen, so the cache is dropped when something changes it, not when a
 *    clock runs out. See invalidateVendors, called from the mutation wrappers
 *    in useTax1099Api so no caller has to remember.
 *  - In-flight requests are shared, so two components mounting together make
 *    one request rather than racing.
 */
export interface VendorPage {
  results?: any[];
  count?: number;
  summary?: any;
  [key: string]: any;
}

export interface VendorListResult {
  rows: any[];
  first: VendorPage;
}

const cache = new Map<string, VendorListResult>();
const inFlight = new Map<string, Promise<VendorListResult>>();

export const vendorCacheKey = (branchId: number | string, taxYear?: number | string | null) =>
  `${branchId}:${taxYear ?? 'default'}`;

export const getCachedVendors = (key: string): VendorListResult | undefined => cache.get(key);

/**
 * Returns the in-flight promise for this key if one exists, otherwise starts
 * `load`, shares it, and caches the result.
 */
export const loadVendorsOnce = (
  key: string,
  load: () => Promise<VendorListResult>,
): Promise<VendorListResult> => {
  const cached = cache.get(key);
  if (cached) {
    return Promise.resolve(cached);
  }
  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }
  const request = load()
    .then((result) => {
      cache.set(key, result);
      return result;
    })
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, request);
  return request;
};

/**
 * Drops cached lists. Called after any mutation that can change a vendor row,
 * its W-9 state or its payments.
 *
 * Clears every year for the branch, not just the current one: a W-9 confirmed
 * today changes last year's readiness too. With no argument it clears
 * everything, which is what a branch switch or a bulk sync wants.
 */
export const invalidateVendors = (branchId?: number | string | null) => {
  if (branchId === undefined || branchId === null) {
    cache.clear();
    inFlight.clear();
    return;
  }
  const prefix = `${branchId}:`;
  Array.from(cache.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => cache.delete(key));
  Array.from(inFlight.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => inFlight.delete(key));
};

/** Test seam. */
export const __resetVendorCache = () => {
  cache.clear();
  inFlight.clear();
};
